import React, { Component } from "react";
import { resetNewsLoginSession } from "./newsLoginSession";

const SESSION_ENDED_MESSAGE_KEY = "assettrakSessionEndedMessage";
const SESSION_STATUS_CHECK_INTERVAL_MS = 60 * 60 * 1000;

let sessionStatusTimer = null;
let sessionRedirectInProgress = false;

export const login = (token, name) => {
  resetNewsLoginSession();
  localStorage.setItem("token", token);
  localStorage.setItem("userName", name);
  sessionRedirectInProgress = false;
};

export const clearSession = () => {
  resetNewsLoginSession();
  window.localStorage.removeItem("token");
  localStorage.removeItem("userName");
  localStorage.removeItem("accountDisplayName");
  localStorage.removeItem("accountEmail");
  localStorage.removeItem("userId");
  localStorage.removeItem("role");
  localStorage.removeItem("customer");
  localStorage.removeItem("realTimeGroupId");
  localStorage.removeItem("realTimeGroupName");
};

export const redirectToLogin = (message) => {
  if (sessionRedirectInProgress) {
    return;
  }

  sessionRedirectInProgress = true;

  if (message) {
    window.sessionStorage.setItem(SESSION_ENDED_MESSAGE_KEY, message);
  }

  clearSession();

  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  } else {
    sessionRedirectInProgress = false;
  }
};

export const logout = (props) => {
  redirectToLogin();
};

export const getSessionEndedMessage = () => {
  const message = window.sessionStorage.getItem(SESSION_ENDED_MESSAGE_KEY);
  window.sessionStorage.removeItem(SESSION_ENDED_MESSAGE_KEY);
  return message;
};

export const isTokenExpired = (token) => {
  if (!token) {
    return true;
  }

  try {
    const base64Payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = base64Payload.padEnd(base64Payload.length + (4 - base64Payload.length % 4) % 4, "=");
    const payload = JSON.parse(window.atob(paddedPayload));
    if (!payload.exp) {
      return false;
    }

    return payload.exp * 1000 <= Date.now();
  } catch (error) {
    return true;
  }
};

export const handleUnauthorizedSession = (error) => {
  if (!error || !error.response || error.response.status !== 401) {
    return false;
  }

  const requestUrl = (error.config && error.config.url) || "";
  const normalisedRequestUrl = requestUrl.toLowerCase();
  if (normalisedRequestUrl.indexOf("/api/auth/login") !== -1
    || normalisedRequestUrl.indexOf("/api/auth/web-login") !== -1) {
    return false;
  }

  redirectToLogin("Your session has ended. Please log in again.");
  return true;
};

export const startSessionStatusPolling = (apiHost, axiosInstance) => {
  if (sessionStatusTimer) {
    clearInterval(sessionStatusTimer);
  }

  const checkSessionStatus = async () => {
    const token = localStorage.getItem("token");
    if (!token || window.location.pathname === "/login") {
      return;
    }

    if (isTokenExpired(token)) {
      redirectToLogin("Your session has ended. Please log in again.");
      return;
    }

    try {
      const response = await axiosInstance.get(`${apiHost}/api/Auth/SessionStatus`, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (!response.data || response.data.status !== 1 || response.data.customerStatus !== 1) {
        redirectToLogin("Your account is no longer active. Please contact your administrator.");
      }
    } catch (error) {
      handleUnauthorizedSession(error);
    }
  };

  window.setTimeout(checkSessionStatus, 1000);
  sessionStatusTimer = setInterval(checkSessionStatus, SESSION_STATUS_CHECK_INTERVAL_MS);
};

function authenticateFactory(ComposedComponent) {
  class Authenticate extends Component {
    constructor(props) {
      super(props);
      this.state = {
        isAuthenticated: false,
      };
    }
    componentDidMount() {
      this._checkAndRedirect();
    }

    _checkAndRedirect() {
      let token = localStorage.getItem("token");
      if (!token) {
        this.setState({ isAuthenticated: false });
        this.props.history.push("/login");
      } else if (isTokenExpired(token)) {
        this.setState({ isAuthenticated: false });
        redirectToLogin("Your session has ended. Please log in again.");
      } else {
        this.setState({ isAuthenticated: true });
      }
    }

    render() {
      return (
        <div>
          {this.state.isAuthenticated ? (
            <ComposedComponent {...this.props} />
          ) : null}
        </div>
      );
    }
  }

  return Authenticate;
}

export default authenticateFactory;
